<template>
  <Teleport to="body">
    <div
      v-if="incoming"
      class="fixed top-4 right-4 z-[100] w-80 rounded-lg border border-white/10 bg-[var(--surface-color,#0d0e12)] shadow-2xl overflow-hidden"
      role="dialog"
      :aria-label="$t('chat.call.incoming_title')"
    >
      <!-- Caller identity + call kind -->
      <div class="p-4 flex items-center gap-3 bg-black/30">
        <img
          :src="incoming.from.avatar || '/images/default-avatar.svg'"
          :alt="incoming.from.name"
          class="w-12 h-12 rounded-full object-cover ring-2 ring-primary/40"
        />
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-white truncate">{{ incoming.from.name }}</p>
          <p class="text-[11px] text-text-muted inline-flex items-center gap-1">
            <el-icon v-if="incoming.kind === 'video'" size="11"><VideoCamera /></el-icon>
            <el-icon v-else size="11"><Phone /></el-icon>
            {{ incoming.kind === 'video' ? $t('chat.call.incoming_video') : $t('chat.call.incoming_audio') }}
            <span v-if="incoming.threadKind === 'group'" class="text-text-dim">· {{ $t('chat.call.group_call') }}</span>
          </p>
        </div>
      </div>

      <!-- Pulse ring (subtle) -->
      <div class="relative flex items-center justify-center py-3 bg-black/20">
        <span class="absolute w-12 h-12 rounded-full bg-primary/30 animate-ping"></span>
        <span class="relative w-8 h-8 rounded-full bg-primary/60 flex items-center justify-center">
          <el-icon size="14" class="text-white"><PhoneFilled /></el-icon>
        </span>
      </div>

      <!-- Actions -->
      <div class="grid grid-cols-2 gap-2 p-3">
        <button
          type="button"
          class="px-3 py-2 rounded-md bg-red-500 text-white text-xs font-semibold hover:bg-red-500/90 transition-colors"
          @click="onReject"
        >
          {{ $t('chat.call.reject') }}
        </button>
        <button
          type="button"
          class="px-3 py-2 rounded-md bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-500/90 transition-colors"
          @click="onAccept"
        >
          {{ $t('chat.call.accept') }}
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { Phone, PhoneFilled, VideoCamera } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useCall } from '~/composables/chat/useCall'

/**
 * Top-right floating toast that rings for 30 s (auto-reject timer lives
 * in `useCall`). Accept provisions local mic/cam and dials into mediasoup;
 * reject tells the caller via the chat WS.
 */
const { t } = useI18n()
const { incoming, accept, reject } = useCall()

function errMessage(err) {
  // useCall.accept() re-throws DOMException (NotAllowedError, NotFoundError, …)
  // and our own preflight codes (call_requires_https). Map the common ones to
  // an i18n key, fall back to a generic message so the toast is never empty.
  const code = err?.name && err.name !== 'Error' ? err.name : (err?.message || '')
  switch (code) {
    case 'NotAllowedError':
    case 'SecurityError':
      return t('chat.call.error.permission_denied')
    case 'NotFoundError':
    case 'OverconstrainedError':
      return t('chat.call.error.no_device')
    case 'call_requires_https':
      return t('chat.call.error.requires_https')
    default:
      return t('chat.call.error.generic')
  }
}

async function onAccept() {
  try {
    await accept()
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[chat:call] accept failed', err)
    ElMessage.error(errMessage(err))
  }
}
function onReject() { reject() }
</script>
